import React from "react";
import { NumericFormat } from "react-number-format";
import { EffektCheckbox } from "../../../../shared/components/EffektCheckbox/EffektCheckbox";
import CharacterCountCircle from "../../../../shared/components/CharacterCountCircle/CharacterCountCircle";
import { SuggestedAmounts } from "../components/SuggestedAmounts";
import { FormData } from "../hooks/useFundraiserForm";
import { getThousandSeparator, getDecimalSeparator } from "../../../../../util/formatting";
import styles from "../FundraiserWidget.module.scss";
import { RadioButtonGroup } from "../../../../shared/components/RadioButton/RadioButtonGroup";
import { RecurringDonation } from "../../../../shared/components/Widget/types/Enums";

interface DonationDetailsPaneProps {
  formData: Pick<FormData, "amount" | "messageSenderName" | "message" | "showName" | "recurring">;
  onChange: (field: keyof FormData, value: any) => void;
  onSubmit: () => void;
  config: {
    suggested_amounts: number[];
    currency_symbol: string;
    donation_amount_label: string;
    name_label: string;
    message_label: string;
    show_name_label: string;
    next_button_text: string;
    monthly_donations?: {
      single_donation_text: string;
      monthly_donation_text: string;
    };
  };
  locale: string;
  className?: string;
  visible?: boolean;
}

export const DonationDetailsPane = React.forwardRef<HTMLDivElement, DonationDetailsPaneProps>(
  ({ formData, onChange, onSubmit, config, locale, className, visible }, ref) => {
    const thousandSeparator = getThousandSeparator(locale);
    const decimalSeparator = getDecimalSeparator(locale);

    return (
      <div
        ref={ref}
        className={className}
        style={{ display: visible ? "block" : "none" }}
        data-cy="fundraiser-pane-donation"
      >
        <form
          className={styles["donation-widget__form"]}
          data-cy="fundraiser-donation-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {config.monthly_donations && (
            <div className={styles["donation-widget__frequency-options"]}>
              <RadioButtonGroup
                options={[
                  {
                    title: config.monthly_donations.single_donation_text,
                    value: RecurringDonation.NON_RECURRING,
                    data_cy: "fundraiser-single-donation-radio",
                  },
                  {
                    title: config.monthly_donations.monthly_donation_text,
                    value: RecurringDonation.RECURRING,
                    data_cy: "fundraiser-monthly-donation-radio",
                  },
                ]}
                selected={formData.recurring}
                onSelect={(value) => onChange("recurring", value)}
              />
            </div>
          )}

          {config.suggested_amounts.length > 0 && (
            <div className={styles["donation-widget__input-group"]}>
              <SuggestedAmounts
                amounts={config.suggested_amounts}
                selectedAmount={formData.amount}
                currencySymbol={config.currency_symbol}
                locale={locale}
                onSelect={(amount) => onChange("amount", amount)}
              />
            </div>
          )}

          <div className={styles["donation-widget__input-group"]}>
            <NumericFormat
              id="amount"
              name="amount"
              value={formData.amount}
              data-cy="fundraiser-amount-input"
              onValueChange={(v) => {
                onChange("amount", v.floatValue ?? null);
              }}
              thousandSeparator={thousandSeparator}
              decimalSeparator={decimalSeparator}
              decimalScale={0}
              allowNegative={false}
              placeholder={config.donation_amount_label}
              required
              min={1}
              step={1}
            />
            <span className={styles["donation-widget__input-group__suffix"]}>
              {config.currency_symbol}
            </span>
          </div>

          <div className={styles["donation-widget__input-group"]} style={{ marginTop: "2rem" }}>
            <input
              id="messageSenderName"
              name="messageSenderName"
              type="text"
              value={formData.messageSenderName}
              onChange={(e) => onChange("messageSenderName", e.target.value)}
              placeholder={config.name_label}
              required={formData.showName}
              maxLength={45}
              data-cy="fundraiser-name-input"
            />
          </div>

          <div className={styles["donation-widget__input-group"]}>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              placeholder={config.message_label}
              onChange={(e) => onChange("message", e.target.value)}
              maxLength={250}
              data-cy="fundraiser-message-input"
            />
            <div className={styles["donation-widget__message-sub"]}>
              {formData.message.length > 0 && (
                <CharacterCountCircle count={formData.message.length} max={250} />
              )}
            </div>
          </div>

          <div className={styles["donation-widget__checkbox-group"]}>
            <EffektCheckbox
              checked={formData.showName}
              onChange={(checked) => {
                onChange("showName", checked);
              }}
              dataCy="fundraiser-show-name"
            >
              {config.show_name_label}
            </EffektCheckbox>
          </div>

          <button
            type="submit"
            className={styles["donation-widget__button"]}
            data-cy="fundraiser-donation-next"
          >
            {config.next_button_text}
          </button>
        </form>
      </div>
    );
  },
);

DonationDetailsPane.displayName = "DonationDetailsPane";
